// Curated YouTube picks shown on the practice launcher, so a first-time
// visitor can start practicing instantly without pasting a link or uploading
// a file. Two groups: evergreen favorites, and a rotating "trending" set.
//
// Each entry can optionally ship ready-made segments (and combos built from
// them). Picking a card loads a real, fully editable project — same as
// pasting the URL yourself, just pre-segmented — so users can tweak the
// splits and save it straight to their library.
//
// TODO(cristina): replace these placeholder entries with real choreography
// videos you have the rights to feature, and set real segment start/end
// times (watch the video and mark the sections you want pre-split — these
// can't be guessed). Swap the "trending" group as trends change — each
// entry is independent, so updating one is a one-line change.
//
// Each entry can also set its own practice defaults — the state the video
// opens in when the card is picked (the user can change any of them and
// save their version to the library):
//
//   defaultMirror: false,                  // original orientation
//   defaultViewCrop: { x: 0.2, y: 0, width: 0.6, height: 1 }, // pre-zoom
//   defaultSpeed: 0.75,                    // start at 0.75x
//   defaultMuted: true,                    // music off
//   defaultComboIndex: 1,                  // loop combos[1] straight away
//   defaultTimelineZoom: 2,                // timeline zoom: 1, 1.5, 2, 3 or 4
//
// Easiest way to author them: open the video, set mirror/crop/speed/mute
// and timeline zoom, start looping the combo you want, then use the dev "Copy as curated
// preset" button — the snippet includes all of the above.

import { Segment, VideoViewCrop, normalizeVideoViewCrop } from '../types';
import { createComboFromSourceIds, getSourceSegments, isCombo } from '../utils';
import { SOURCE_SEGMENT_COLOR } from '../../constants';

export interface CuratedSegmentPreset {
  name: string;
  startTime: number;
  endTime: number;
  transitionDuration?: number;
}

/** Curated combo presets are capped at this many source segments — kept
 * short (transition/preview clips), not a full-song run-through. */
export const CURATED_COMBO_MAX_SEGMENTS = 2;

export interface CuratedComboPreset {
  name?: string;
  /** 0-based indices into this entry's `segments` array. At most
   * CURATED_COMBO_MAX_SEGMENTS — longer presets are skipped at build time. */
  sourceSegmentIndices: number[];
}

export interface CuratedChoreography {
  videoId: string;
  /** Start offset in seconds, e.g. to jump straight to the chorus. */
  startTime?: number;
  title: string;
  /** Ready-made segment splits, pre-loaded (and fully editable) on pick. */
  segments?: CuratedSegmentPreset[];
  /** Ready-made combos built from `segments`, by index. */
  combos?: CuratedComboPreset[];

  // ---- Per-video practice defaults (all optional). They only set the
  // starting state when the card is picked — the user can change any of
  // them and save the result to their own library. ----

  /** Start mirrored (true) or in the original orientation (false). */
  defaultMirror?: boolean;
  /** Pre-zoom on an area of interest. Fractions 0..1 of the video frame,
   * same shape the in-app crop tool saves (`{ x, y, width, height }`). */
  defaultViewCrop?: VideoViewCrop;
  /** Starting playback speed, e.g. 0.75. Snapped to a YouTube-supported rate. */
  defaultSpeed?: number;
  /** Start with the music muted (true) or on (false). */
  defaultMuted?: boolean;
  /** 0-based index into this entry's `combos` array — that combo is
   * selected and starts looping as soon as the video loads. */
  defaultComboIndex?: number;
  /** Starting timeline zoom (1 = fit whole video; the app's levels are
   * 1, 1.5, 2, 3, 4 — other values snap to the nearest one). */
  defaultTimelineZoom?: number;
}

/** Resolved starting state for a curated pick, handed to the player. */
export interface CuratedPracticeDefaults {
  isMirrored?: boolean;
  viewCrop?: VideoViewCrop;
  playbackRate?: number;
  isMuted?: boolean;
  /** Id of the built combo segment to select and loop on load. */
  activeComboId?: string;
  timelineZoom?: number;
}

export interface CuratedPracticePreset {
  segments: Segment[];
  defaults: CuratedPracticeDefaults;
}

export const ALL_TIME_CHOREOGRAPHIES: CuratedChoreography[] = [
  {
    videoId: 'dQw4w9WgXcQ',
    title: 'All-Time Favorite 1',
    segments: [
      { name: 'Intro', startTime: 0, endTime: 15 },
      { name: 'Verse', startTime: 15, endTime: 40 },
      { name: 'Chorus', startTime: 40, endTime: 65 },
    ],
    combos: [
      { name: 'Verse + Chorus', sourceSegmentIndices: [1, 2] },
    ],
  },
  { videoId: 'dQw4w9WgXcQ', title: 'All-Time Favorite 2' },
  { videoId: 'dQw4w9WgXcQ', title: 'All-Time Favorite 3' },
];

// All three entries below use real, hand-marked segment timestamps (watched
// in-app and exported via the "Copy as curated preset" dev tool).
export const TRENDING_CHOREOGRAPHIES: CuratedChoreography[] = [
  {
    videoId: '5sDaIO__b-g',
    title: '(Full Focused) ALPHA DRIVE ONE(알파드라이브원) \'BORN DIRE\' (4K) | STUDIO CHOOM ORIGINAL',
    segments: [
      { name: 'Segment 1', startTime: 0, endTime: 9.28 },
      { name: 'Segment 2', startTime: 9.28, endTime: 14.98 },
      { name: 'Segment 3', startTime: 14.98, endTime: 20.88 },
      { name: 'Segment 4', startTime: 20.88, endTime: 29.58 },
      { name: 'Segment 5', startTime: 29.58, endTime: 36.28 },
      { name: 'Segment 6', startTime: 36.28, endTime: 43.69 },
      { name: 'Segment 7', startTime: 43.69, endTime: 51.69 },
      { name: 'Segment 8', startTime: 51.69, endTime: 58.99 },
      { name: 'Segment 9', startTime: 58.99, endTime: 65.78 },
      { name: 'Segment 10', startTime: 65.78, endTime: 74.52 },
      { name: 'Segment 11', startTime: 74.52, endTime: 80.74 },
      { name: 'Segment 12', startTime: 80.74, endTime: 87.35 },
      { name: 'Segment 13', startTime: 87.35, endTime: 94.85 },
      { name: 'Segment 14', startTime: 94.85, endTime: 103.04 },
      { name: 'Segment 15', startTime: 103.04, endTime: 110.45 },
      { name: 'Segment 16', startTime: 110.45, endTime: 116.56 },
      { name: 'Segment 17', startTime: 116.56, endTime: 123.85 },
      { name: 'Segment 18', startTime: 123.85, endTime: 132.35 },
      { name: 'Segment 19', startTime: 132.35, endTime: 139.49 },
      { name: 'Segment 20', startTime: 139.49, endTime: 147.23 },
      { name: 'Segment 21', startTime: 147.23, endTime: 153.52 },
      { name: 'Segment 22', startTime: 153.52, endTime: 163.61 },
    ],
    combos: [
      { name: 'Chorus', sourceSegmentIndices: [6,9] },
    ],
    defaultMirror: true,
    defaultViewCrop: { x: 0.1294, y: 0.1511, width: 0.747, height: 0.7436 },
    defaultSpeed: 0.75,
    defaultMuted: false,
    defaultTimelineZoom: 2,
    defaultComboIndex: 0, 
  },
    {
    videoId: '2LArv4SztL8',
    title: 'EVAN (에반) \'Death of Me\' Dance Practice | Stag:E',
    segments: [
      { name: 'Part 1', startTime: 0, endTime: 13.96 },
      { name: 'Segment 2', startTime: 13.96, endTime: 23.25 },
      { name: 'Segment 3', startTime: 23.25, endTime: 29.19 },
      { name: 'Segment 4', startTime: 29.19, endTime: 33.5 },
      { name: 'Segment 5', startTime: 33.5, endTime: 41.6 },
      { name: 'Segment 6', startTime: 41.6, endTime: 49.9 },
      { name: 'Segment 7', startTime: 49.9, endTime: 58 },
      { name: 'Segment 8', startTime: 58, endTime: 64.82 },
      { name: 'Segment 9', startTime: 64.82, endTime: 71.66 },
      { name: 'Segment 10', startTime: 71.66, endTime: 76.66 },
      { name: 'Segment 11', startTime: 76.66, endTime: 83.97 },
      { name: 'Segment 12', startTime: 83.97, endTime: 89.31 },
      { name: 'Segment 13', startTime: 89.31, endTime: 101.18 },
      { name: 'Segment 14', startTime: 101.18, endTime: 109.36 },
      { name: 'Segment 15', startTime: 109.36, endTime: 119.18 },
      { name: 'Segment 16', startTime: 119.18, endTime: 132.63 },
      { name: 'Segment 17', startTime: 132.63, endTime: 139.16 },
      { name: 'Segment 18', startTime: 139.16, endTime: 144.97 },
      { name: 'Segment 19', startTime: 144.97, endTime: 150.47 },
      { name: 'Segment 20', startTime: 150.47, endTime: 156.77 },
      { name: 'Segment 21', startTime: 156.77, endTime: 162.96 },
      { name: 'Segment 22', startTime: 162.96, endTime: 167.14 },
      { name: 'Segment 23', startTime: 167.14, endTime: 175.55 },
      { name: 'Segment 24', startTime: 175.55, endTime: 179.7 },
      { name: 'Segment 25', startTime: 179.7, endTime: 185.84 },
    ],
    combos: [
      { sourceSegmentIndices: [8, 12] },
      { sourceSegmentIndices: [18, 19] },
    ],
    defaultMirror: true,
    defaultViewCrop: { x: 0.1087, y: 0.0659, width: 0.7568, height: 0.7533 },
    defaultSpeed: 0.75,
    defaultMuted: false,
    defaultTimelineZoom: 2,
    defaultComboIndex: 0, 
  },
    {
    videoId: '1Yu-Q8SGOBo',
    title: 'aespa 에스파 \'LEMONADE\' Dance Practice',
    segments: [
      { name: 'Segment 1', startTime: 0, endTime: 8.347427087738037 },
      { name: 'Segment 2', startTime: 8.347427087738037, endTime: 16.146638921798704 },
      { name: 'Segment 3', startTime: 16.146638921798704, endTime: 23.648118078201293 },
      { name: 'Segment 4', startTime: 23.648118078201293, endTime: 31.049000190734862 },
      { name: 'Segment 5', startTime: 31.049000190734862, endTime: 40.35407592752075 },
      { name: 'Segment 6', startTime: 40.35407592752075, endTime: 49.64554887411499 },
      { name: 'Segment 7', startTime: 49.64554887411499, endTime: 54.95 },
      { name: 'Segment 8', startTime: 54.95, endTime: 61.047166032424926 },
      { name: 'Segment 9', startTime: 61.047166032424926, endTime: 66.34646702670288 },
      { name: 'Segment 10', startTime: 66.34646702670288, endTime: 70.95881590653991 },
      { name: 'Segment 11', startTime: 70.95881590653991, endTime: 78.1572387844696 },
      { name: 'Segment 12', startTime: 78.1572387844696, endTime: 88.17 },
      { name: 'Segment 13', startTime: 88.17, endTime: 94.44929189891052 },
      { name: 'Segment 14', startTime: 94.44929189891052, endTime: 101.84730788746643 },
      { name: 'Segment 15', startTime: 101.84730788746643, endTime: 109.45198286839295 },
      { name: 'Segment 16', startTime: 109.45198286839295, endTime: 116.85143908392334 },
      { name: 'Segment 17', startTime: 116.85143908392334, endTime: 125.14941801907348 },
      { name: 'Segment 18', startTime: 125.14941801907348, endTime: 128.65768194659424 },
      { name: 'Segment 19', startTime: 128.65768194659424, endTime: 131.7548320190735 },
      { name: 'Segment 20', startTime: 131.7548320190735, endTime: 139.9499209294281 },
      { name: 'Segment 21', startTime: 139.9499209294281, endTime: 147.34740607629394 },
      { name: 'Segment 22', startTime: 147.34740607629394, endTime: 152.7563831411438 },
      { name: 'Segment 23', startTime: 152.7563831411438, endTime: 160.35408787602233 },
      { name: 'Segment 24', startTime: 160.35408787602233, endTime: 166.24906804768372 },
      { name: 'Segment 25', startTime: 166.24906804768372, endTime: 172.45735797901918 },
      { name: 'Segment 26', startTime: 172.45735797901918, endTime: 177.3494880858307 },
      { name: 'Segment 27', startTime: 177.3494880858307, endTime: 186.65203399809266 },
    ],
    combos: [
      { sourceSegmentIndices: [9, 10] },
      { sourceSegmentIndices: [7, 11] },
    ],
    defaultMirror: true,
    defaultViewCrop: { x: 0.1618, y: 0.122, width: 0.6623, height: 0.6565 },
    defaultSpeed: 0.75,
    defaultMuted: false,
    defaultTimelineZoom: 2,
    defaultComboIndex: 1,
  },
    {
    videoId: 'RXQCY_zxz-E',
    title: 'Hearts2Hearts 하츠투하츠 \'RUDE!\' Dance Practice',
    segments: [
      { name: 'Segment 1', startTime: 0, endTime: 15.28979904196167 },
      { name: 'Segment 2', startTime: 15.28979904196167, endTime: 22.989041885559082 },
      { name: 'Segment 3', startTime: 22.989041885559082, endTime: 31.791018074386596 },
      { name: 'Segment 4', startTime: 31.791018074386596, endTime: 38.70182688746643 },
      { name: 'Segment 5', startTime: 38.70182688746643, endTime: 45.4916388588562 },
      { name: 'Segment 6', startTime: 45.4916388588562, endTime: 56.96 },
      { name: 'Segment 7', startTime: 56.96, endTime: 65.47449292370605 },
      { name: 'Segment 8', startTime: 65.47449292370605, endTime: 75.57425305912781 },
      { name: 'Segment 9', startTime: 75.57, endTime: 102.04 },
      { name: 'Segment 10', startTime: 102.04, endTime: 104.32 },
      { name: 'Segment 11', startTime: 104.32, endTime: 106.41 },
      { name: 'Segment 12', startTime: 106.41, endTime: 110.37 },
      { name: 'Segment 13', startTime: 110.37, endTime: 113.58 },
      { name: 'Segment 14', startTime: 113.58, endTime: 117.79 },
      { name: 'Segment 15', startTime: 117.79, endTime: 120.77 },
      { name: 'Segment 16', startTime: 120.77, endTime: 125.15 },
      { name: 'Segment 17', startTime: 125.15, endTime: 128.73 },
      { name: 'Segment 18', startTime: 128.73, endTime: 134.17 },
      { name: 'Segment 19', startTime: 134.17, endTime: 136.88 },
      { name: 'Segment 20', startTime: 136.88, endTime: 139.68 },
      { name: 'Segment 21', startTime: 139.68, endTime: 145.68 },
      { name: 'Segment 22', startTime: 145.68, endTime: 153.58 },
      { name: 'Segment 23', startTime: 153.58, endTime: 167.88 },
      { name: 'Segment 24', startTime: 167.88, endTime: 172.98 },
      { name: 'Segment 25', startTime: 172.98, endTime: 176.17 },
      { name: 'Segment 26', startTime: 176.17, endTime: 180.68 },
      { name: 'Segment 27', startTime: 180.68, endTime: 187.78 },
      { name: 'Segment 28', startTime: 187.78, endTime: 191.18 },
      { name: 'Segment 29', startTime: 191.18, endTime: 199.28 },
    ],
    combos: [
      { sourceSegmentIndices: [10,18] },
    ],
    defaultMirror: true,
    defaultSpeed: 0.75,
    defaultMuted: false,
    defaultTimelineZoom: 3,
    defaultComboIndex: 0,
  },
    {
    videoId: 'cxhqqpVk65Q',
    title: 'BABYMONSTER - ‘DRIP’ PERFORMANCE VIDEO',
    segments: [
      { name: 'Segment 1', startTime: 0, endTime: 29.75 },
      { name: 'Segment 2', startTime: 29.75, endTime: 36.48 },
      { name: 'Segment 3', startTime: 36.48, endTime: 41.4 },
      { name: 'Segment 4', startTime: 41.4, endTime: 48.4 },
      { name: 'Segment 5', startTime: 48.4, endTime: 55.19 },
      { name: 'Segment 6', startTime: 55.19, endTime: 60.99 },
      { name: 'Segment 7', startTime: 60.99, endTime: 69.08 },
      { name: 'Segment 8', startTime: 69.08, endTime: 77.19 },
      { name: 'Segment 9', startTime: 77.19, endTime: 83.28 },
      { name: 'Segment 10', startTime: 83.28, endTime: 87.69 },
      { name: 'Segment 11', startTime: 87.69, endTime: 95.78 },
      { name: 'Segment 12', startTime: 95.78, endTime: 103.28 },
      { name: 'Segment 13', startTime: 103.28, endTime: 107.98 },
      { name: 'Segment 14', startTime: 107.98, endTime: 111.87 },
      { name: 'Segment 15', startTime: 111.87, endTime: 119.08 },
      { name: 'Segment 16', startTime: 119.08, endTime: 125.78 },
      { name: 'Segment 17', startTime: 125.78, endTime: 134.98 },
      { name: 'Segment 18', startTime: 134.98, endTime: 142.88 },
      { name: 'Segment 19', startTime: 142.88, endTime: 151.08 },
      { name: 'Segment 20', startTime: 151.08, endTime: 163.38 },
      { name: 'Segment 21', startTime: 163.38, endTime: 169.48 },
      { name: 'Segment 22', startTime: 169.48, endTime: 175.06 },
      { name: 'Segment 23', startTime: 175.06, endTime: 183.67 },
    ],
    combos: [
      { sourceSegmentIndices: [5, 7] },
    ],
    defaultMirror: true,
    defaultSpeed: 0.75,
    defaultMuted: false,
    defaultTimelineZoom: 2,
    defaultComboIndex: 0,
  },
    {
    videoId: 'rAE2y9Y3EV8',
    title: 'aespa 에스파 \'WDA (Whole Different Animal) (Feat. G-DRAGON)\' Dance Practice',
    segments: [
      { name: 'Segment 1', startTime: 0, endTime: 13.470252047683715 },
      { name: 'Segment 2', startTime: 13.470252047683715, endTime: 17.872408143051146 },
      { name: 'Segment 3', startTime: 17.872408143051146, endTime: 24.272221 },
      { name: 'Segment 4', startTime: 24.272221, endTime: 32.07240608964538 },
      { name: 'Segment 5', startTime: 32.07240608964538, endTime: 41.07306811825561 },
      { name: 'Segment 6', startTime: 41.07306811825561, endTime: 50.373899066757204 },
      { name: 'Segment 7', startTime: 50.373899066757204, endTime: 59.9730860076294 },
      { name: 'Segment 8', startTime: 59.9730860076294, endTime: 65.7732998588562 },
      { name: 'Segment 9', startTime: 65.7732998588562, endTime: 69.57291918692016 },
      { name: 'Segment 10', startTime: 69.57291918692016, endTime: 75.27295992752076 },
      { name: 'Segment 11', startTime: 75.27295992752076, endTime: 82.97210497901916 },
      { name: 'Segment 12', startTime: 82.97, endTime: 89.54 },
      { name: 'Segment 13', startTime: 89.54, endTime: 99.62 },
      { name: 'Segment 14', startTime: 99.62, endTime: 106.54 },
      { name: 'Segment 15', startTime: 106.54, endTime: 111.54 },
      { name: 'Segment 16', startTime: 111.54, endTime: 120.54 },
      { name: 'Segment 17', startTime: 120.54, endTime: 129.44 },
      { name: 'Segment 18', startTime: 129.44, endTime: 139.14 },
      { name: 'Segment 19', startTime: 139.14, endTime: 148.74 },
    ],
    combos: [
      { sourceSegmentIndices: [6, 7] },
      { sourceSegmentIndices: [8, 9] },
      { sourceSegmentIndices: [14, 15] },
      { sourceSegmentIndices: [0, 1] },
      { sourceSegmentIndices: [12, 13] },
      { sourceSegmentIndices: [14, 15] },
      { sourceSegmentIndices: [15, 16] },
      { sourceSegmentIndices: [7, 10] },
    ],
    defaultMirror: true,
    defaultViewCrop: { x: 0.2304, y: 0.2518, width: 0.5218, height: 0.5209 },
    defaultSpeed: 0.75,
    defaultMuted: false,
    defaultTimelineZoom: 2,
    defaultComboIndex: 7,
  },

];

export function getYouTubeThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
}

/**
 * Materializes a curated entry's segment/combo presets into real Segment
 * objects (fresh ids, so repeated picks never collide). Returns [] when the
 * entry has no presets — the card just opens a blank, normal project.
 */
export function buildSegmentsFromCuratedChoreography(item: CuratedChoreography): Segment[] {
  return buildCuratedPracticePreset(item).segments;
}

/**
 * Builds the segments/combos for a curated pick plus its resolved practice
 * defaults (mirror, crop, speed, mute, and which combo to loop on load).
 */
export function buildCuratedPracticePreset(item: CuratedChoreography): CuratedPracticePreset {
  const defaults: CuratedPracticeDefaults = {};
  if (item.defaultMirror !== undefined) defaults.isMirrored = item.defaultMirror;
  const viewCrop = normalizeVideoViewCrop(item.defaultViewCrop);
  if (viewCrop) defaults.viewCrop = viewCrop;
  if (item.defaultSpeed !== undefined && item.defaultSpeed > 0) defaults.playbackRate = item.defaultSpeed;
  if (item.defaultMuted !== undefined) defaults.isMuted = item.defaultMuted;
  if (item.defaultTimelineZoom !== undefined && item.defaultTimelineZoom > 0) {
    defaults.timelineZoom = item.defaultTimelineZoom;
  }

  const presetSegments = item.segments ?? [];
  if (presetSegments.length === 0) return { segments: [], defaults };

  const runId = Date.now();
  const segments: Segment[] = presetSegments.map((preset, index) => ({
    id: `segment-${runId}-${index}`,
    name: preset.name,
    startTime: preset.startTime,
    endTime: preset.endTime,
    color: SOURCE_SEGMENT_COLOR,
    transitionDuration: preset.transitionDuration ?? 0.5,
    isLearned: false,
    isDisabled: false,
  }));

  const combos = (item.combos ?? [])
    .map((comboPreset, index) => {
      if (comboPreset.sourceSegmentIndices.length > CURATED_COMBO_MAX_SEGMENTS) {
        console.warn(
          `Curated combo preset for "${item.title}" spans ${comboPreset.sourceSegmentIndices.length} segments; curated combos are capped at ${CURATED_COMBO_MAX_SEGMENTS}. Skipping it.`
        );
        return null;
      }

      const sourceIds = comboPreset.sourceSegmentIndices
        .map((i) => segments[i]?.id)
        .filter((id): id is string => Boolean(id));

      const combo = createComboFromSourceIds(
        segments,
        sourceIds,
        0.5,
        `combo-${runId}-${index}`
      );
      if (!combo) return null;
      return comboPreset.name
        ? { ...combo, name: comboPreset.name, comboNameEdited: true }
        : combo;
    });

  if (item.defaultComboIndex !== undefined) {
    const defaultCombo = combos[item.defaultComboIndex];
    if (defaultCombo) {
      defaults.activeComboId = defaultCombo.id;
    } else {
      console.warn(
        `Curated defaultComboIndex ${item.defaultComboIndex} for "${item.title}" does not match a valid combo. Ignoring it.`
      );
    }
  }

  return {
    segments: [...segments, ...combos.filter((combo): combo is Segment => combo !== null)],
    defaults,
  };
}

/**
 * Inverse of `buildSegmentsFromCuratedChoreography`: turns the segments/combos
 * a real user just marked (via the normal segment editor, watching the actual
 * video) back into paste-ready `CuratedChoreography` source code. Used by the
 * in-app "Copy as curated preset" dev tool so presets are authored by
 * actually watching the video, not guessed.
 */
export function formatCuratedChoreographyForClipboard(
  videoId: string,
  title: string,
  startTime: number | undefined,
  segments: Segment[],
  defaults: CuratedPracticeDefaults = {}
): string {
  const sourceSegments = getSourceSegments(segments);
  const indexById = new Map(sourceSegments.map((segment, index) => [segment.id, index]));

  const quote = (value: string) => `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;

  const lines: string[] = ['  {', `    videoId: ${quote(videoId)},`];
  if (startTime) lines.push(`    startTime: ${startTime},`);
  lines.push(`    title: ${quote(title)},`);

  if (sourceSegments.length > 0) {
    lines.push('    segments: [');
    for (const segment of sourceSegments) {
      const transition = segment.transitionDuration !== 0.5
        ? `, transitionDuration: ${segment.transitionDuration}`
        : '';
      lines.push(
        `      { name: ${quote(segment.name)}, startTime: ${segment.startTime}, endTime: ${segment.endTime}${transition} },`
      );
    }
    lines.push('    ],');
  }

  const combos = segments
    .filter(isCombo)
    .map(combo => {
      const sourceSegmentIndices = (combo.sourceSegmentIds ?? [])
        .map(id => indexById.get(id))
        .filter((index): index is number => index !== undefined)
        .sort((a, b) => a - b);
      return { combo, sourceSegmentIndices };
    })
    .filter(({ sourceSegmentIndices }) => sourceSegmentIndices.length >= 2);

  if (combos.length > 0) {
    lines.push('    combos: [');
    for (const { combo, sourceSegmentIndices } of combos) {
      const nameField = combo.comboNameEdited ? `name: ${quote(combo.name)}, ` : '';
      lines.push(`      { ${nameField}sourceSegmentIndices: [${sourceSegmentIndices.join(', ')}] },`);
    }
    lines.push('    ],');
  }

  if (defaults.isMirrored !== undefined) lines.push(`    defaultMirror: ${defaults.isMirrored},`);
  const viewCrop = normalizeVideoViewCrop(defaults.viewCrop);
  if (viewCrop) {
    const round = (value: number) => Math.round(value * 10000) / 10000;
    lines.push(
      `    defaultViewCrop: { x: ${round(viewCrop.x)}, y: ${round(viewCrop.y)}, width: ${round(viewCrop.width)}, height: ${round(viewCrop.height)} },`
    );
  }
  if (defaults.playbackRate !== undefined) lines.push(`    defaultSpeed: ${defaults.playbackRate},`);
  if (defaults.isMuted !== undefined) lines.push(`    defaultMuted: ${defaults.isMuted},`);
  if (defaults.timelineZoom !== undefined && defaults.timelineZoom !== 1) {
    lines.push(`    defaultTimelineZoom: ${defaults.timelineZoom},`);
  }
  const defaultComboIndex = defaults.activeComboId
    ? combos.findIndex(({ combo }) => combo.id === defaults.activeComboId)
    : -1;
  if (defaultComboIndex >= 0) lines.push(`    defaultComboIndex: ${defaultComboIndex},`);

  lines.push('  },');
  return lines.join('\n');
}
